import React from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import flow from 'lodash/flow';
import classNames from 'classnames';
import {
    showWidget,
    createBetCard,
    createThreeMarketBetCard,
    createDescriptionTemplate,
    getMarketHeader,
} from '../../../../../shared/utils/widgets.js';
import {
    getGaAttributeValueHomepageWidget,
    GA_COMPONENT_UNKNOWN_COMPONENT_VALUE,
} from '~shared/utils/google-analytics';
import WidgetContainer from '../../../../../shared/ui/WidgetContainer';
import ContentSection from '../../../../../shared/components/react/ContentSection';
import {
    SPORTS_WIDGET_TARGET_CLASS,
    POST_URL,
    WIDGET_LABEL,
    WIDGET_GTM_SUFFIX,
    WIDGET_GTM_FOOTER,
    COLUMN_HEADERS,
    THREE_MARKET_COLUMN_HEADERS,
} from '../constants';
import {
    mapStateToProps,
    mapDispatchToProps,
} from '../../CommonDataListEvents/utils/utils.js';
import { FEATURED_MATCHES_BASE_CLASS } from '../../../../../shared/components/react/FeaturedMatchesWidget/constants';
import '../styles/sports-widget.scss';
import '../../CommonDataListEvents/styles/featured-matches-widget.scss';
import { seoUrlSport } from '../../../../../spa/Utils/SeoUrls.js';
import {
    getSportIdBySlug,
    getHasBoostedOddsSelections,
} from '~shared/utils/sportsbook';
import { withLazyLoadContext } from '~spa/Components/LazyLoad/LazyLoad.jsx';
import WidgetQuickLinks from '~shared/components/widget-quick-links';
import { isGenericSportV2 } from '~spa/Pages/SportGenericV2/utils.js';
import TournamentEventsSectionLazy from '~scenes/sport/generic/matches/components/TournamentEventsSection';
import { groupEventsByDate } from '~scenes/sport/generic/matches/utils.js';

export class SportsWidgetContainer extends React.Component {
    constructor() {
        super();

        this.contentSectionParams = null;
        this.marketHeaderLabel = null; // no sense in computing each time

        this.getHeaderLabelFromEvents = this.getHeaderLabelFromEvents.bind(this);
        this.createContentSection = this.createContentSection.bind(this);
    }

    static propTypes = {
        sport: PropTypes.string,
        events: PropTypes.oneOfType([
            PropTypes.object,
            PropTypes.array,
        ]),
        getWidgetEvents: PropTypes.func,
        commonDataEvents: PropTypes.object,
        updateCommonStoreWsData: PropTypes.func,
        stopListeningForUpdates: PropTypes.func,
        source: PropTypes.string,
        isFavouriteSport: PropTypes.bool,
        lazyLoadHasLoaded: PropTypes.func,
        metaData: PropTypes.object,
    };

    static defaultProps = {
        sport: '',
        events: {},
        getWidgetEvents: () => null,
        commonDataEvents: {},
        updateCommonStoreWsData: () => null,
        stopListeningForUpdates: () => null,
        source: '',
        isFavouriteSport: false,
        lazyLoadHasLoaded: () => null,
        metaData: {},
    };

    /**
     * Look through widget events for a market header label, set this.marketHeaderLabel and return same.
     * If all markets DO NOT contain the same market name, then we set an empty string.
     * @param {Object} events from commonDataStore
     * @returns {String} the label
     */
    getHeaderLabelFromEvents(events) {
        if (this.marketHeaderLabel !== null) {

            return this.marketHeaderLabel;
        }

        const marketHeaderSet = Object.values(events).reduce((headersSet, { markets = {} }) => {
            Object.values(markets).forEach(({ name }) => { headersSet.add(name); });

            return headersSet;
        }, new Set);
        const marketHeaders = [...marketHeaderSet];
        this.marketHeaderLabel = marketHeaders.length === 1
            ? marketHeaders[0]
            : '';

        return this.marketHeaderLabel;
    }

    /**
     * Create a ContentSection element containing the list of events in the specified group
     * @param {Object} events The object with events data
     * @return {ContentSection} The ContentSection element
     */
    createContentSection(events) {
        // common events info, sport slug and source
        const {
            commonDataEvents: commonEvents,
            sport,
            source,
        } = this.props;
        // match state event ids with commonData event ids
        // NB: This assumes that all events in the common store belong to this widget. THIS MIGHT NOT BE THE
        // CASE IN THE FUTURE IF MULTIPLE COMPONENTS SHARE THE SAME STORE
        const commonEventsKeys = Object.keys(commonEvents);
        // widget events
        const widgetEvents = events.filter((event) => commonEventsKeys.includes(String(event.event_id)));

        // get events/markets ids and isolate the first of each
        const [firstEvent] = widgetEvents;
        const { grouped_market_ids: marketIds, market_id: defaultMarketId } = firstEvent;
        // this assumes that the selection_schema for all markets is the same, e.g. all 2-way or all 3-way
        const { markets } = commonEvents[firstEvent.event_id];
        const defaultMarket = markets[defaultMarketId];
        const { usesThreeMarketView } = commonEvents[firstEvent.event_id];
        const headerLabel = usesThreeMarketView ? '' : this.getHeaderLabelFromEvents(commonEvents);

        const titleModifiers = [COLUMN_HEADERS];
        if (usesThreeMarketView) {
            titleModifiers.push(THREE_MARKET_COLUMN_HEADERS);
        }
        // configuration for content section
        if (this.contentSectionParams === null) {
            // we assume that the headers will not change. If we ever have a widget that displays more than
            // one selection_type/type (i.e. 2 way/3 way) this will be incorrect. Issue is that we do not get
            // the market name in a WS inplay_start message (which triggers an event transitioning from PP to IP)
            // for now we use '' if we have more than one market in the widget events - see
            // this.getHeaderLabelFromEvents()
            this.contentSectionParams = {
                headerText: getMarketHeader(sport, headerLabel, defaultMarket, usesThreeMarketView),
                isCollapsible: false,
                titleModifiers,
            };
        }

        return (
            <ContentSection params={this.contentSectionParams}>
                {widgetEvents.map((event) => {
                    const { event_id: key } = event;
                    const eventObj = commonEvents[event.event_id];
                    const [marketId] = Object.keys(eventObj.markets);
                    const defaultMarket = eventObj.markets[marketId];
                    const hasBoostedOddsSelections = Boolean(marketId) && getHasBoostedOddsSelections(defaultMarket.selections);
                    const gtmAttribute = `${source};${sport}${WIDGET_GTM_SUFFIX};`;
                    const descriptionTemplate = createDescriptionTemplate(eventObj, true, gtmAttribute, hasBoostedOddsSelections);

                    return usesThreeMarketView
                    ? createThreeMarketBetCard(
                        eventObj,
                        marketIds,
                        key, // React key
                        descriptionTemplate,
                        gtmAttribute,
                    )
                    : createBetCard(
                        eventObj,
                        marketId,
                        key, // React key
                        descriptionTemplate,
                        gtmAttribute,
                    );
                })}
            </ContentSection>
        );
    }

    /**
     * After init DOM component, load page data
     * @returns {void}
     */
    componentDidMount() {
       this.refreshBets(true);
    }

    componentWillUnmount() {
        this.props.stopListeningForUpdates();
    }

    async refreshBets(fromBE) {
        if (fromBE) {
            const postParams = {
                widget_label: WIDGET_LABEL,
            };
            await this.props.getWidgetEvents(this.props.updateCommonStoreWsData, `${POST_URL}/${this.props.sport}`, postParams);
        } else {
            const waitMs = 500;
            await new Promise(resolve => setTimeout(() => {
                resolve();
            }, waitMs));
        }

        this.props.lazyLoadHasLoaded();
        this.refreshBets.fetched = true;
    }

    /**
     * Render the component
     * @returns {JSX.Element} Returns a React Element
     */
    render() {
        const {
            source,
            sport,
            isFavouriteSport = false,
            commonDataEvents,
            events,
            metaData: {
                widgetQuickLinks: widgetQuickLinksData = [],
            },
        } = this.props;
        if (isEmpty(commonDataEvents) || isEmpty(events) || !showWidget(events, commonDataEvents)) {
            return null;
        }

        const isGenericSportWidget = isGenericSportV2(sport);
        const gtm = `${source};${sport}${WIDGET_GTM_SUFFIX};${WIDGET_GTM_FOOTER}`;
        const betLocateValue = source === 'homepage'
            ? getGaAttributeValueHomepageWidget(sport)
            : GA_COMPONENT_UNKNOWN_COMPONENT_VALUE;

        /**
         * The solution to get the sport name and sport slug is only a hack.
         *
         * Client was crashing when the first event from the widget was offed via WebSockets.
         * commonDataListEvents DOES NOT update when an event is offed.
         *
         * The better solution is to provide the sport slug and name from the endpoint,
         * then we won't need perform mappings on each render. Simple as that.
         */
        let sportName = null;
        let localizedSportSlug = null;
        for (const event of events) {
            let { event_id } = event;
            if (typeof commonDataEvents[event_id] === 'undefined') {
                continue;
            }

            ({ sport_name: sportName, sport_slug_i18n: localizedSportSlug } = commonDataEvents[event_id]);
            break;
        }

        if (!sportName || !localizedSportSlug) {
            return null;
        }

        /**
         * We have to use Featured Matches additional class
         * in order to overwrite some stylings.
         * Originally the 2nd class was added in PHP only
         * and featured-matches-widget SCSS imported within sports-widget SCSS.
         * It's going to be changed (fixed) by Ruby Team during skinning project.
         */
        const containerClasses = classNames(
            SPORTS_WIDGET_TARGET_CLASS,
            FEATURED_MATCHES_BASE_CLASS
        );

        // only isGenericSportWidget support PP -> IP transitions
        const filteredEvents = this.props.events
            .filter(({ addedFromWs = false }) => (isGenericSportWidget || !addedFromWs));
        if (!filteredEvents.length) {

            return null;
        }

        let sportWidgetEventsList = [];
        if (isGenericSportWidget) {
            const mergedEventsData = filteredEvents.map(event => {
                return {...commonDataEvents[event.event_id], ...event};
            });

            const groupedEventsList = Object.values(groupEventsByDate(mergedEventsData));
            sportWidgetEventsList = groupedEventsList.map(({ id, events }) => {
                return (
                    <TournamentEventsSectionLazy
                        key={id}
                        eventsList={events}
                        sportSlug={sport}
                        showDateHeader={true}
                        showTournamentHeader={false}
                    />
                );
            });
        }

        return (
            <div className={containerClasses}>
                <WidgetContainer
                    title={sportName}
                    titleIconClassName={this.props.sport}
                    source={source}
                    sportSlug={sport}
                    gtmAttribute={gtm}
                    gaComponent={betLocateValue}
                    isFavouriteSport={isFavouriteSport}
                    titleLink={seoUrlSport(localizedSportSlug, getSportIdBySlug(this.props.sport))}
                >
                    <WidgetQuickLinks data={widgetQuickLinksData} sportSlug={sport} />
                    {
                        isGenericSportWidget
                            ? sportWidgetEventsList
                            : this.createContentSection(filteredEvents)
                    }
                </WidgetContainer>
            </div>
        );
    }
}

export default flow(
    withLazyLoadContext(),
    connect(mapStateToProps, mapDispatchToProps),
)(SportsWidgetContainer);
